import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import InputFloating from '@/components/atoms/InputFloating';
import { Button } from '@/components/ui/button';

import PageWrapper from '@/components/atoms/PageWrapper';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useUpdatePassword } from '@/features/user/hooks/useMutation/useUpdatePassword';

const schema = z.object({
    newPassword: z.string()
        .min(6, { message: "Your password must be at least 6 characters long" }),
    passwordConfirmation: z.string()
})
    .refine((data) => data.newPassword === data.passwordConfirmation, {
        message: "The passwords do not match",
        path: ["passwordConfirmation"]
    });

export default function UpdatePassword() {
    const navigate = useNavigate();

    const { mutateAsync: updatePassword, isPending } = useUpdatePassword();

    type schemaType = z.infer<typeof schema>;
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<schemaType>({
        resolver: zodResolver(schema),
    });

    const onSubmit: SubmitHandler<schemaType> = async (formData) => {
        if (isPending) return;

        try {
            const response = await updatePassword(formData.newPassword);
            if (response.error) {
                ErrorPopup();
            } else {
                SuccessPopup("Success! Your password has been successfully changed.", 2500);
                navigate('/login');
            }
        } catch (err) {
            ErrorPopup("An error occurred. Please check your internet connection");
        }
    };

    return (
        <PageWrapper className='flex flex-col items-center flex-1 w-full max-w-full custom_bg'>
            {isPending && <LoaderWithBackdrop />}
            <div className="justify-center w-full max-w-xl px-3 py-8 rounded sm:px-8 bg-gray-50/80">
                <div className="mb-4">
                    <h1 className="font-bold text-center">Enter your new password</h1>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-2">
                        <InputFloating
                            label="New Password"
                            type="password"
                            errorMessage={errors?.newPassword?.message?.toString()}
                            showError={true}
                            register={register("newPassword")}
                        />
                        <InputFloating
                            label="Confirm Password"
                            type="password"
                            errorMessage={errors?.passwordConfirmation?.message?.toString()}
                            showError={true}
                            register={register("passwordConfirmation")}
                        />
                    </div>

                    <div className="flex justify-end mt-4">
                        <div className="w-full sm:w-auto">
                            <Button className="w-full" type="submit" size={'sm'}>Confirm</Button>
                        </div>
                    </div>
                </form>
            </div>
        </PageWrapper>
    );
}
